/**
 * Lot AQ : règles de la zone d'images (`components/ImageDropZone.vue`), en logique pure testée : types acceptés,
 * taille et nombre au plus, messages en français, libellés. Rien de propre aux retours : le lot AS la réutilise.
 */

/** Images matricielles acceptées par défaut (le SVG n'en fait pas partie : il peut contenir du script). */
export const DEFAULT_IMAGE_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
];

export interface ImageRules {
  maxCount: number;
  maxBytes: number;
  types: readonly string[];
}

/** Ce qu'il faut d'un fichier pour les contrôles (un `File` convient). */
export interface FileLike {
  name: string;
  type: string;
  size: number;
  lastModified?: number;
}

const TYPE_OF_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

const TYPE_LABELS: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/heic': 'HEIC',
  'image/heif': 'HEIC',
};

function extensionOf(name: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(name);
  return match ? match[1]!.toLowerCase() : '';
}

/**
 * Type d'image retenu pour un fichier, ou null s'il n'est pas accepté. Sans type annoncé (fréquent pour les HEIC
 * de l'iPhone sur un ordinateur), l'extension décide.
 */
export function acceptedImageType(
  file: FileLike,
  types: readonly string[] = DEFAULT_IMAGE_TYPES,
): string | null {
  const announced = file.type.toLowerCase();
  if (announced) return types.includes(announced) ? announced : null;
  const guessed = TYPE_OF_EXTENSION[extensionOf(file.name)];
  return guessed && types.includes(guessed) ? guessed : null;
}

/** Les HEIC / HEIF n'ont pas d'aperçu dans la plupart des navigateurs : une icône les remplace. */
export function canPreview(file: FileLike): boolean {
  const type = acceptedImageType(file);
  return type !== null && type !== 'image/heic' && type !== 'image/heif';
}

/** « 820 o », « 412 Ko », « 1,2 Mo ». */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  const mo = bytes / (1024 * 1024);
  return `${(Math.round(mo * 10) / 10).toLocaleString('fr-FR')} Mo`;
}

/** « PNG, JPEG, GIF, WebP ou HEIC ». */
export function typesLabel(types: readonly string[] = DEFAULT_IMAGE_TYPES): string {
  const labels = [...new Set(types.map((t) => TYPE_LABELS[t] ?? t.replace(/^image\//, '').toUpperCase()))];
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} ou ${labels.at(-1)}` : (labels[0] ?? '');
}

/** « encore 3 images possibles », « encore 1 image possible », « plus de place ». */
export function remainingLabel(count: number, maxCount: number): string {
  const left = Math.max(0, maxCount - count);
  if (left === 0) return 'plus de place';
  return left === 1 ? 'encore 1 image possible' : `encore ${left} images possibles`;
}

function sameFile(a: FileLike, b: FileLike): boolean {
  return a.name === b.name && a.size === b.size && a.type === b.type && a.lastModified === b.lastModified;
}

/**
 * Ajoute des fichiers à la sélection en appliquant les règles : type, taille, nombre. Un fichier déjà présent
 * (même nom, taille, type et date) est ignoré sans message. Renvoie la nouvelle sélection et les messages.
 */
export function addImages<F extends FileLike>(
  current: readonly F[],
  incoming: readonly F[],
  rules: ImageRules,
): { files: F[]; errors: string[] } {
  const files = [...current];
  const errors: string[] = [];
  let overflow = 0;
  for (const file of incoming) {
    if (files.some((f) => sameFile(f, file))) continue;
    if (!acceptedImageType(file, rules.types)) {
      errors.push(`« ${file.name} » : ce fichier n'est pas une image (${typesLabel(rules.types)}).`);
    } else if (file.size > rules.maxBytes) {
      errors.push(`« ${file.name} » : image trop lourde (${formatBytes(rules.maxBytes)} au plus).`);
    } else if (files.length >= rules.maxCount) {
      overflow += 1;
    } else {
      files.push(file);
    }
  }
  if (overflow > 0) {
    errors.push(
      `${rules.maxCount} images au plus : ${overflow === 1 ? '1 image n’a pas été ajoutée' : `${overflow} images n’ont pas été ajoutées`}.`,
    );
  }
  return { files, errors };
}

/** Une capture collée arrive souvent nommée « image.png » : elle devient « capture-<n>.png ». */
export function pastedFileName(name: string, type: string, index: number): string {
  if (name && !/^image\.[a-z0-9]+$/i.test(name)) return name;
  const extension = extensionOf(name) || (type.split('/')[1] ?? 'png').replace('jpeg', 'jpg');
  return `capture-${index}.${extension}`;
}

/** Le glisser transporte-t-il des fichiers (et pas un texte ou un lien) ? */
export function carriesFiles(transfer: Pick<DataTransfer, 'types'> | null | undefined): boolean {
  return !!transfer && Array.from(transfer.types ?? []).includes('Files');
}

/** Fichiers d'un glisser-déposer ou d'un collage (certains navigateurs ne remplissent que `items`). */
export function filesOf(transfer: Pick<DataTransfer, 'files' | 'items'> | null | undefined): File[] {
  if (!transfer) return [];
  const files = Array.from(transfer.files ?? []);
  if (files.length > 0) return files;
  return Array.from(transfer.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
}
